/**
 * Generic type to create transient props from a type
 *
 * Transient props are a concept from styled-components. They are normal props
 * prefixed with a $ symbol. This prevents styled-components from adding them
 * to an element as HTML attribute.
 *
 * This type takes a type T and prefixes its keys with $, essentially making
 * all its properties transient props.
 */
export type TransientProps<T> = {
  [K in keyof T as K extends string ? `$${K}` : never]: T[K];
};

/**
 * Transform an object's key to styled-component transient props format
 *
 * This utility function prefixes an object's keys with a $, turning it's
 * properties into transient props for styled-components.
 *
 * Transient props are regular props, but the $ prevents styled-components from
 * adding them as HTML attribute to the HTML element.
 */
export const transientProps = <P extends object>(
  props: P
): TransientProps<P> => {
  return (Object.keys(props) as (keyof P)[]).reduce(
    (newProps, oldProp) => {
      if (typeof oldProp === 'string') {
        newProps[`$${oldProp}`] = props[oldProp];
      } else {
        newProps[oldProp] = props[oldProp];
      }

      return newProps;
    },
    {} as { [key: string | symbol]: unknown }
  ) as TransientProps<P>;
};
